import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Edit } from 'lucide-react';
import { formatNumber } from '../../utils/constants';
import { getCategoryIcon, API_URL } from './helpers';

export const AssetsTab = ({ assets, assetCategories, isAdmin, canDeleteAsset, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newAsset, setNewAsset] = useState({ category: '', name: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
  const [editingAsset, setEditingAsset] = useState(null);

  const assetsByCategory = assets.reduce((acc, asset) => {
    if (!acc[asset.category]) acc[asset.category] = [];
    acc[asset.category].push(asset);
    return acc;
  }, {});

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/assets`, {
        ...newAsset,
        amount: parseFloat(newAsset.amount),
        date: new Date(newAsset.date).toISOString()
      });
      toast.success('Asset created successfully');
      setDialogOpen(false);
      setNewAsset({ category: '', name: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create asset');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingAsset) return;
    try {
      await axios.put(`${API_URL}/assets/${editingAsset.id}`, {
        category: editingAsset.category,
        name: editingAsset.name,
        description: editingAsset.description,
        amount: parseFloat(editingAsset.amount),
        date: new Date(editingAsset.date).toISOString()
      });
      toast.success('Asset updated successfully');
      setEditDialogOpen(false);
      setEditingAsset(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await axios.delete(`${API_URL}/assets/${id}`);
      toast.success('Asset deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete asset');
    }
  };

  const openEdit = (asset) => {
    setEditingAsset({
      ...asset,
      date: asset.date ? asset.date.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setEditDialogOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Other Assets (Manual Entries)</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-asset-btn"><Plus className="w-4 h-4 mr-2" />Add Asset</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Asset</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <Select value={newAsset.category} onValueChange={(v) => setNewAsset({...newAsset, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {assetCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input value={newAsset.name} onChange={(e) => setNewAsset({...newAsset, name: e.target.value})} placeholder="e.g., Opening Balance, Office Computer" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea value={newAsset.description} onChange={(e) => setNewAsset({...newAsset, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input type="number" step="0.01" value={newAsset.amount} onChange={(e) => setNewAsset({...newAsset, amount: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date *</label>
                <Input type="date" value={newAsset.date} onChange={(e) => setNewAsset({...newAsset, date: e.target.value})} required />
              </div>
              <Button type="submit" className="w-full">Create Asset</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Summary Cards */}
      {Object.keys(assetsByCategory).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {assetCategories.filter(cat => assetsByCategory[cat]).map(category => {
            const categoryTotal = assetsByCategory[category]?.reduce((sum, a) => sum + a.amount, 0) || 0;
            return (
              <div key={category} className="p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center space-x-2 mb-2">
                  {getCategoryIcon(category)}
                  <span className="text-sm font-medium text-green-800">{category}</span>
                </div>
                <p className="text-lg font-bold text-green-700">RWF {formatNumber(categoryTotal)}</p>
                <p className="text-xs text-green-600">{assetsByCategory[category]?.length || 0} items</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Assets Table */}
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="assets-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Category</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Description</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Date</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {assets.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No manual assets recorded yet.</td></tr>
            )}
            {assets.map((asset) => (
              <tr key={asset.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center space-x-2">
                    {getCategoryIcon(asset.category)}
                    <span>{asset.category}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-foreground">{asset.name}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{asset.description || '-'}</td>
                <td className="px-4 py-3 text-sm font-semibold text-green-600">RWF {formatNumber(asset.amount)}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {asset.date ? new Date(asset.date).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(asset)}><Edit className="w-4 h-4" /></Button>
                        {canDeleteAsset && <Button variant="ghost" size="sm" onClick={() => handleDelete(asset.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Asset Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Asset</DialogTitle></DialogHeader>
          {editingAsset && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <Select value={editingAsset.category} onValueChange={(v) => setEditingAsset({...editingAsset, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {assetCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input value={editingAsset.name} onChange={(e) => setEditingAsset({...editingAsset, name: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea value={editingAsset.description || ''} onChange={(e) => setEditingAsset({...editingAsset, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input type="number" step="0.01" value={editingAsset.amount} onChange={(e) => setEditingAsset({...editingAsset, amount: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date *</label>
                <Input type="date" value={editingAsset.date} onChange={(e) => setEditingAsset({...editingAsset, date: e.target.value})} required />
              </div>
              <Button type="submit" className="w-full">Update Asset</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
